import { useSearchParams } from 'react-router';
import { AppSheet } from '@/components/ui/app-sheet';
import { EmptyState } from '@/components/ui/empty-state';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { VPN_FLOWS } from '@/features/vpnFlows';
import type { RegionVpn } from '../api';
import { RegionVpnBadge } from '../components/Badges';
import { RegionVpnPanel } from '../components/RegionVpnPanel';
import { useRegionVpns } from '../hooks';

const sessionsHeld = (r: RegionVpn) => r.gateways.reduce((n, g) => n + (g.vpn.report?.sessions ?? 0), 0);

const columns: ResponsiveTableColumn<RegionVpn>[] = [
  { key: 'name', label: 'Site', hideMobileLabel: true, render: (r) => r.regionName },
  { key: 'vpn', label: 'VPN', render: (r) => <RegionVpnBadge region={r} /> },
  {
    key: 'gateways',
    label: 'Gateways running it',
    render: (r) => (
      <span className="tabular-nums">
        {String(r.gateways.filter((g) => g.vpn.report?.on).length)} of {String(r.gateways.length)}
      </span>
    ),
  },
  {
    key: 'dns',
    label: 'DNS servers',
    hideAt: 'mobile',
    render: (r) => <span className="text-xs">{r.dnsServers.join(', ') || "Each gateway's own"}</span>,
  },
  { key: 'sessions', label: 'Sessions', align: 'right', render: (r) => <span className="tabular-nums">{sessionsHeld(r)}</span> },
  ...(VPN_FLOWS
    ? [
        {
          key: 'flows',
          label: 'Flow records waiting',
          align: 'right' as const,
          hideAt: 'mobile' as const,
          render: (r: RegionVpn) => (
            <span className="tabular-nums">{r.gateways.reduce((n, g) => n + (g.vpn.report?.flowsQueued ?? 0), 0)}</span>
          ),
        },
      ]
    : []),
];

/**
 * Every site's VPN (docs/VpnAccess.md, "Slice 7 design"): a region is the
 * network technicians reach, and any of its gateways can carry them. A row
 * opens the region's switch, DNS servers and gateways; `?region=` opens one.
 */
export function VpnSites() {
  const [params, setParams] = useSearchParams();
  const { data, isLoading, isError } = useRegionVpns();
  const openId = params.get('region');
  const open = (data ?? []).find((r) => r.regionId === openId) ?? null;
  const setOpen = (regionId: string | null) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (regionId) next.set('region', regionId);
        else next.delete('region');
        return next;
      },
      { replace: true },
    );
  };

  return (
    <PageContainer width="full">
      <PageHeader title="Sites" subtitle="Where the VPN is on, and which gateways run it" />
      <ResponsiveTable
        columns={columns}
        rows={data ?? []}
        rowKey={(r) => r.regionId}
        isLoading={isLoading}
        onRowClick={(r) => {
          setOpen(r.regionId);
        }}
        empty={
          isError ? (
            <EmptyState variant="error" size="inline" title="Couldn't load the sites" />
          ) : (
            <EmptyState size="inline" title="No sites" description="Regions you can see appear here." />
          )
        }
      />
      {open ? (
        <AppSheet
          open
          onOpenChange={(o) => {
            if (!o) setOpen(null);
          }}
          size="lg"
          title={open.regionName}
          description="The VPN for this site"
        >
          <AppSheet.Body>
            <RegionVpnPanel regionId={open.regionId} />
          </AppSheet.Body>
        </AppSheet>
      ) : null}
    </PageContainer>
  );
}
